import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import FilterModel from 'app/components/FilterModel';
import HeaderWithBackButton from 'app/components/HeaderWithBackButton';
import PageLoader from 'app/components/PageLoader';
import Button from 'app/components/button';
import PaginatedTable from 'app/components/table/PaginationTable';
import { APP_CONSTANTS } from 'app/constants/app-constants';
import useBannerAnalytics from './useBannerAnalytics';
import { buildBannerAnalyticsColumns } from './bannerAnalyticsColumns';

// Page layout: back-arrow title, then one row with the banner thumbnail +
// the banner's own mall name (context only, never a column or filter) +
// total clicks on the left and Filter / Export on the right, then the
// table. The date range lives in the shared Filter side panel, as on
// Voucher Management.
const BannerThumbnail = ({ imgUrl, alt }) => {
  const isRemoteUrl = typeof imgUrl === 'string' && /^https?:\/\//.test(imgUrl);
  if (!isRemoteUrl) return null;
  const isVideo = /\.mp4$/i.test(imgUrl);
  return isVideo ? (
    <video controls width="100" height="100">
      <source crossOrigin="anonymous" src={imgUrl} type="video/mp4" />
    </video>
  ) : (
    <img
      src={imgUrl}
      alt={alt}
      crossOrigin="anonymous"
      style={{ width: '100px', maxHeight: '200px' }}
    />
  );
};

const BannerAnalytics = () => {
  const location = useLocation();
  const {
    navigate,
    rows,
    count,
    summary,
    loading,
    dataPerPage,
    sortBy,
    sortOrder,
    onSort,
    fetchData,
    draftStartDate,
    draftEndDate,
    setDraftStartDate,
    setDraftEndDate,
    dateError,
    handleReset,
    isFilterOpen,
    setIsFilterOpen,
    handleFilterSubmit,
    handleFilterCancel,
    downloadInProgress,
    handleDownload,
  } = useBannerAnalytics();

  const columns = useMemo(
    () => buildBannerAnalyticsColumns({ sortBy, sortOrder, onSort }),
    [sortBy, sortOrder, onSort]
  );

  const goBackToBannerList = () =>
    navigate(APP_CONSTANTS.MALL_BANNER, {
      state: { mall_id: location?.state?.mall_id || summary?.mall_id },
    });

  return (
    <div className="cms-content__container">
      <HeaderWithBackButton
        title="Banner Analytics"
        goTo={APP_CONSTANTS.MALL_BANNER}
        onBackClicked={goBackToBannerList}
      />

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        {summary ? (
          <div className="d-flex align-items-center flex-wrap">
            <BannerThumbnail imgUrl={summary.img_url} alt={summary.mallName} />
            <div className="ms-3">
              <div>
                <strong>Mall:</strong> {summary.mallName || '-'}
              </div>
              <div>
                <strong>Total clicks:</strong> {summary.clickCount ?? 0}
              </div>
              {/* Click-through rate, all time like Total clicks: customers
                  who opened the link ÷ customers who viewed or opened it. */}
              <div>
                <strong>CTR:</strong>{' '}
                {summary.ctrPercent === null || summary.ctrPercent === undefined
                  ? '-'
                  : `${summary.ctrPercent}% (${summary.clickedCustomers} of ${summary.reachedCustomers} customers)`}
              </div>
            </div>
          </div>
        ) : (
          <div />
        )}
        {/* Same shared Button + classes as the SectionHeader buttons on
            Voucher Management, placed here so they sit on the right. */}
        <div>
          <Button
            className="bg-white margin-right-class"
            onClick={() => setIsFilterOpen((prev) => !prev)}
          >
            Filter
          </Button>
          <Button
            className="bg-white"
            onClick={handleDownload}
            isDisabled={downloadInProgress}
          >
            {downloadInProgress ? 'Exporting...' : 'Export'}
          </Button>
        </div>
      </div>

      <PaginatedTable
        columns={columns}
        data={rows}
        fetchData={fetchData}
        paginationHandler={fetchData}
        totalNumber={count}
        dataPerPage={dataPerPage}
      />

      {/* Date range is the only filter (no mall or tier). */}
      {isFilterOpen && (
        <FilterModel
          isFilterOpen={isFilterOpen}
          setIsFilterOpen={setIsFilterOpen}
          onSubmit={handleFilterSubmit}
          onCancel={handleFilterCancel}
          resetfilter={handleReset}
          onHideFilter={handleFilterCancel}
        >
          <div className="offcanvas-item">
            <label>Start Date</label>
            <input
              type="date"
              value={draftStartDate}
              max={draftEndDate || undefined}
              onChange={(e) => setDraftStartDate(e.target.value)}
            />
          </div>
          <div className="offcanvas-item">
            <label>End Date</label>
            <input
              type="date"
              value={draftEndDate}
              min={draftStartDate || undefined}
              onChange={(e) => setDraftEndDate(e.target.value)}
            />
          </div>
          {dateError && <p className="loginWrapper-valid-text">{dateError}</p>}
        </FilterModel>
      )}

      {loading && <PageLoader pageLoading={loading} />}
    </div>
  );
};

export default BannerAnalytics;
